import { Database, GitBranch } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";

interface EnvironmentAnalyticsProps {
  servicesCount: number;
  runningServices: number;
  deploymentsCount: number;
  failedDeployments: number;
  /** Route to the most recent failed deployment — makes the stat actionable. */
  failedDeploymentLink?: string;
  loading?: boolean;
}

export function EnvironmentAnalytics({
  servicesCount,
  runningServices,
  deploymentsCount,
  failedDeployments,
  failedDeploymentLink,
  loading = false,
}: EnvironmentAnalyticsProps) {
  if (loading) {
    // Skeletons that match the two stat cards (no "Loading…" text flash).
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-20" />
            </CardHeader>
            <CardContent className="space-y-2">
              <Skeleton className="h-7 w-12" />
              <Skeleton className="h-3 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Services</CardTitle>
          <Database className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-h2">{servicesCount}</div>
          {/* Running count reads green when there's at least one healthy service. */}
          <p className="text-xs text-muted-foreground">
            <span
              className={cn(
                "font-medium",
                runningServices > 0 ? "text-success" : "text-muted-foreground"
              )}
            >
              {runningServices} running
            </span>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Deployments</CardTitle>
          <GitBranch className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-h2">{deploymentsCount}</div>
          {/* Failures read red so a broken environment stands out; when we know
              which deployment failed, the stat links straight to its logs. */}
          <p className="text-xs text-muted-foreground">
            {failedDeployments > 0 && failedDeploymentLink ? (
              <Link
                to={failedDeploymentLink}
                className="font-medium text-destructive underline-offset-2 hover:underline"
              >
                {failedDeployments} failed — view logs
              </Link>
            ) : (
              <span
                className={cn(
                  "font-medium",
                  failedDeployments > 0
                    ? "text-destructive"
                    : "text-muted-foreground"
                )}
              >
                {failedDeployments} failed
              </span>
            )}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
